import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "../useData";
import { resolveImg } from "../api";
import SEO from "../components/SEO";

const Section = ({ title, children }) => (
  <section className="mt-14 border-t border-[color:var(--line)] pt-8 grid grid-cols-12 gap-6 md:gap-10">
    <h2 className="col-span-12 md:col-span-3 font-serif text-[color:var(--ink-mute)] text-[13px] uppercase tracking-wider">
      {title}
    </h2>
    <div className="col-span-12 md:col-span-9">{children}</div>
  </section>
);

const YearList = ({ items, useTitle = false }) => (
  <ul className="divide-y divide-[color:var(--line)]">
    {items.map((row, i) => (
      <li key={i} className="grid grid-cols-12 gap-4 items-baseline py-2.5">
        <span className="col-span-3 md:col-span-2 font-serif text-[13px] text-[color:var(--ink-mute)] counter">
          {row.year}
        </span>
        <span className="col-span-9 md:col-span-10 font-serif text-[14px]">
          {useTitle ? row.title : row.entry}
        </span>
      </li>
    ))}
  </ul>
);

const About = () => {
  const artist = useSettings();
  if (!artist) return null;
  return (
    <div className="px-6 md:px-10 max-w-[1000px] mx-auto pb-10">
      <SEO
        title="About & CV"
        description="Alice Lisi — photographer based in Milano. Selected clients include Disney, Vogue, L'Officiel, Loro Piana, Moncler, Mytheresa. Exhibited at Fahey/Klein Gallery, Maison de la Photographie and Fondazione Cavallerizza Reale."
        path="/about"
        image={artist.portrait}
      />
      {/* Statement + portrait */}
      <section className="grid grid-cols-12 gap-6 md:gap-10 pt-2">
        <div className="col-span-12 md:col-span-4">
          <div className="aspect-square bg-[color:var(--paper-2)] overflow-hidden">
            <img
              src={resolveImg(artist.portrait || "https://customer-assets-7cd3h4nn.emergentagent.net/job_creator-store-78/artifacts/qzblbkoh_F1430004.webp")}
              alt="Alice Lisi"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 20%" }}
            />
          </div>
        </div>
        <div className="col-span-12 md:col-span-8">
          {artist.statement.split("\n\n").map((para, i) => (
            <p key={i} className="font-serif text-[17px] md:text-[19px] leading-[1.55] mb-4">
              {para}
            </p>
          ))}
        </div>
      </section>

      {/* Books */}
      <Section title="Books">
        <YearList items={artist.books} useTitle />
      </Section>

      {/* Selected Exhibitions */}
      <Section title="Selected Exhibitions">
        <YearList items={artist.selectedExhibitions} />
      </Section>

      {/* Publications */}
      <Section title="Publications">
        <YearList items={artist.publications} useTitle />
      </Section>

      {/* Education */}
      <Section title="Education">
        <YearList items={artist.cv} />
      </Section>

      {/* Workshops & Mentorships */}
      <Section title="Workshops & Mentorships">
        <ul className="space-y-2">
          {artist.workshops.map((w, i) => (
            <li key={i} className="font-serif text-[14px]">{w}</li>
          ))}
        </ul>
      </Section>

      {/* Commercial clients */}
      <Section title="Commercial">
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5">
          {artist.clients.map((c) => (
            <li key={c} className="font-serif text-[14px]">{c}</li>
          ))}
        </ul>
      </Section>

      {/* Editorial & Music */}
      <Section title="Editorial & Music">
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5">
          {artist.editorialMusic.map((c) => (
            <li key={c} className="font-serif text-[14px]">{c}</li>
          ))}
        </ul>
      </Section>

      {/* Selected Collaborations */}
      <Section title="Selected Collaborations">
        <p className="font-serif text-[14px] leading-[1.7]">
          Assistant Photographer to{" "}
          {artist.assistantTo.map((name, i, arr) => (
            <React.Fragment key={name}>
              <span className="whitespace-nowrap">{name}</span>
              {i < arr.length - 2 ? ", " : i === arr.length - 2 ? " and " : "."}
            </React.Fragment>
          ))}
        </p>
      </Section>

      {/* Instagram + contact CTA */}
      <div className="mt-20 pt-8 border-t border-[color:var(--line)] flex flex-wrap items-baseline justify-between gap-4">
        <div className="flex flex-wrap gap-5">
          <a href={`mailto:${artist.email}`} className="font-serif text-[13px] tlink">{artist.email}</a>
          <a href={artist.instagramUrl} target="_blank" rel="noreferrer" className="font-serif text-[13px] tlink">{artist.instagram}</a>
        </div>
        <Link to="/contact" className="font-serif text-[13px] tlink">Contact form →</Link>
      </div>
    </div>
  );
};

export default About;
